import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";

/* =========================================================
   CONFIG
========================================================= */

const API_KEY =
  "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";

/* =========================================================
   HELPERS
========================================================= */

const buildImageUrl = (baseUrl, path) => {
  if (!path) return "";

  if (
    path.startsWith("http://") ||
    path.startsWith("https://") ||
    path.startsWith("data:")
  ) {
    return path;
  }

  const base = String(baseUrl || "").replace(/\/+$/, "");
  const imagePath = String(path || "").replace(/^\/+/, "");

  return `${base}/${imagePath}`;
};

const getPaymentLink = (element) => {
  if (!element) return "#";

  if (element.ap_type === "local") {
    return `/local-deposit-view/${element.id}/${element.slug}`;
  }

  if (element.ap_type === "newpayment") {
    return `/crypto-deposit-view/${element.id}/${element.slug}`;
  }

  return "/casino-providers";
};

/* =========================================================
   IMAGE COMPONENT
========================================================= */

const PaymentImage = ({
  src,
  alt,
  className = "",
  fallbackText = "Payment",
}) => {
  const [imageError, setImageError] = useState(false);

  if (!src || imageError) {
    return (
      <div className={`pd-image-fallback ${className}`}>
        <span>💳</span>
        <small>{fallbackText}</small>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt || "Payment"}
      className={className}
      loading="lazy"
      onError={() => setImageError(true)}
    />
  );
};

/* =========================================================
   PAYMENT METHOD CARD
========================================================= */

const PaymentMethodCard = ({ element, BaseUrl }) => {
  const link = getPaymentLink(element);

  const imageUrl = buildImageUrl(BaseUrl, element?.photo);

  return (
    <Link
      to={link}
      className="pd-method-link"
      aria-label={`Open ${element?.sort_discription || "payment method"}`}
    >
      <div className="pd-method-card">
        <div className="pd-method-top">
          <div className="pd-method-image-wrap">
            <PaymentImage
              src={imageUrl}
              alt={element?.sort_discription}
              className="pd-method-image"
              fallbackText="Method"
            />
          </div>

          <div className="pd-method-arrow">
            <span>→</span>
          </div>
        </div>

        <div className="pd-method-content">
          <h4>
            {element?.sort_discription || "Payment Method"}
          </h4>

          <div className="pd-method-footer">
            <span className="pd-available">
              <i></i>
              Available
            </span>

            <span className="pd-select-text">
              Select
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

const PostDetails = () => {
  const { BaseUrl } = GlobalSet();

  const [loading, setLoading] = useState(true);
  const [getway, setGetway] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  /* =========================================================
     FETCH PAYMENT METHODS
  ========================================================= */

  useEffect(() => {
    let mounted = true;

    const fetchPaymentMethods = async () => {
      try {
        setLoading(true);
        setError("");

        const data = {
          key_s: API_KEY,
        };

        const response = await axios.post("/apps-deposit", data);

        if (!mounted) return;

        const result = response?.data?.data;

        if (Array.isArray(result)) {
          setGetway(result);
        } else {
          setGetway([]);
        }
      } catch (error) {
        console.error("Deposit method load error:", error);

        if (!mounted) return;

        setError(
          error?.response?.data?.message ||
            "Unable to load payment methods. Please try again."
        );

        setGetway([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchPaymentMethods();

    return () => {
      mounted = false;
    };
  }, []);

  /* =========================================================
     FILTER
  ========================================================= */

  const filteredGateways = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) {
      return getway;
    }

    return getway
      .map((gateway) => {
        const paymentType = String(
          gateway?.payment_type || ""
        ).toLowerCase();

        const matches = Array.isArray(gateway?.allmatch)
          ? gateway.allmatch.filter((method) => {
              const description = String(
                method?.sort_discription || ""
              ).toLowerCase();

              const type = String(
                method?.ap_type || ""
              ).toLowerCase();

              return (
                description.includes(keyword) ||
                type.includes(keyword) ||
                paymentType.includes(keyword)
              );
            })
          : [];

        if (paymentType.includes(keyword)) {
          return gateway;
        }

        if (matches.length > 0) {
          return {
            ...gateway,
            allmatch: matches,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [getway, search]);

  /* =========================================================
     TOTAL METHODS
  ========================================================= */

  const totalMethods = useMemo(() => {
    return getway.reduce((total, gateway) => {
      return (
        total +
        (Array.isArray(gateway?.allmatch)
          ? gateway.allmatch.length
          : 0)
      );
    }, 0);
  }, [getway]);

  /* =========================================================
     LOADING
  ========================================================= */

  if (loading) {
    return (
      <>
        <SmartStyles />

        <div  className="pd-page">
          <div className="pd-container">

            <div className="pd-loading-header">
              <div className="pd-skeleton pd-skeleton-title"></div>
              <div className="pd-skeleton pd-skeleton-text"></div>
            </div>

            <div className="pd-loading-section">
              <div className="pd-skeleton pd-skeleton-section"></div>

              <div className="pd-loading-grid">
                {[1, 2, 3, 4].map((item) => (
                  <div
                    className="pd-skeleton pd-skeleton-card"
                    key={item}
                  ></div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </>
    );
  }

  /* =========================================================
     ERROR
  ========================================================= */

  if (error) {
    return (
      <>
        <SmartStyles />

        <div>
          <div className="pd-container">
            <div className="pd-state-card">
              <div className="pd-state-icon">
                !
              </div>

              <h3>Payment Methods Unavailable</h3>

              <p>{error}</p>

              <button
                type="button"
                className="pd-retry-btn"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* =========================================================
     VIEW
  ========================================================= */

  return (
    <>
      <SmartStyles />

      <div>
        <div className="pd-container">

          {/* =================================================
              SEARCH
          ================================================= */}

          {totalMethods > 4 && (
            <div className="pd-search-box">

              <div className="pd-search-icon">
                ⌕
              </div>

              <input
                type="text"
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
                placeholder="Search payment method..."
              />

              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="pd-search-clear"
                >
                  ×
                </button>
              )}

            </div>
          )}

          {/* =================================================
              PAYMENT GROUPS
          ================================================= */}

          {filteredGateways.length === 0 ? (
            <div className="pd-state-card">

              <div className="pd-state-icon">
                ⌕
              </div>

              <h3>No Payment Method Found</h3>

              <p>
                We couldn't find a payment method matching
                "{search}".
              </p>

              <button
                type="button"
                className="pd-retry-btn"
                onClick={() => setSearch("")}
              >
                Clear Search
              </button>

            </div>
          ) : (
            <div className="pd-gateway-list">

              {filteredGateways.map(
                (getwayitem, gatewayIndex) => {

                  const methods = Array.isArray(
                    getwayitem?.allmatch
                  )
                    ? getwayitem.allmatch
                    : [];

                  return (
                    <section
                      className="pd-gateway-section"
                      key={
                        getwayitem?.id ||
                        `gateway-${gatewayIndex}`
                      }
                    >

                      {/* =========================
                          GROUP HEADER
                      ========================= */}

                      <div className="pd-group-header">

                        <div className="pd-group-info">

                          <div className="pd-group-logo">

                            <PaymentImage
                              src={buildImageUrl(
                                BaseUrl,
                                getwayitem?.photo
                              )}
                              alt={
                                getwayitem?.payment_type
                              }
                              className="pd-group-logo-img"
                              fallbackText=""
                            />

                          </div>

                          <div>
                            <span className="pd-group-label">
                              Payment Category
                            </span>

                            <h2>
                              {getwayitem?.payment_type ||
                                "Payment Method"}
                            </h2>
                          </div>

                        </div>

                        <div className="pd-method-count">
                          {methods.length}{" "}
                          {methods.length === 1
                            ? "Method"
                            : "Methods"}
                        </div>

                      </div>

                      {/* =========================
                          METHODS
                      ========================= */}

                      {methods.length > 0 ? (
                        <div className="pd-method-grid">

                          {methods.map(
                            (element, methodIndex) => (
                              <PaymentMethodCard
                                key={
                                  element?.id ||
                                  `method-${methodIndex}`
                                }
                                element={element}
                                BaseUrl={BaseUrl}
                              />
                            )
                          )}

                        </div>
                      ) : (
                        <div className="pd-empty-method">
                          No payment method is currently
                          available in this category.
                        </div>
                      )}

                    </section>
                  );
                }
              )}

            </div>
          )}

          {/* =================================================
              FOOTER INFO
          ================================================= */}

          <div className="pd-security">

            <div className="pd-security-icon">
              ✓
            </div>

            <div>
              <strong>
                Secure Payment Selection
              </strong>

              <p>
                Select a payment method to continue to
                the next step. Always verify payment
                information before confirming a transaction.
              </p>
            </div>

          </div>

        </div>
      </div>
    </>
  );
};

/* =========================================================
   CSS
========================================================= */

const SmartStyles = () => (
  <style>{`

    /* =====================================================
       ROOT
    ===================================================== */

    .pd-page {
      --pd-primary: #635bff;
      --pd-primary-dark: #5148e5;
      --pd-primary-soft: #f0efff;

      --pd-green: #12b76a;
      --pd-green-soft: #ecfdf3;

      --pd-text: #182230;
      --pd-muted: #667085;

      --pd-border: #e8eaf0;
      --pd-background: #f7f8fb;

      min-height: 100vh;

      background:
        radial-gradient(
          circle at 0 0,
          rgba(99,91,255,.08),
          transparent 27%
        ),
        radial-gradient(
          circle at 100% 10%,
          rgba(18,183,106,.05),
          transparent 24%
        ),
        var(--pd-background);

      padding: 25px;

      font-family:
        Inter,
        -apple-system,
        BlinkMacSystemFont,
        "Segoe UI",
        sans-serif;

      color: var(--pd-text);
    }

    .pd-container {
      width: 100%;
      max-width: 1250px;
      margin: 0 auto;
    }


    /* =====================================================
       HERO
    ===================================================== */

    .pd-hero {

      position: relative;
      overflow: hidden;

      display: flex;
      align-items: center;
      justify-content: space-between;

      gap: 25px;

      padding: 30px;

      margin-bottom: 20px;

      border-radius: 24px;

      background:
        linear-gradient(
          135deg,
          #171b2d 0%,
          #252b48 58%,
          #383466 100%
        );

      box-shadow:
        0 18px 50px rgba(31,38,65,.12);

    }

    .pd-hero::after {

      content: "";

      position: absolute;

      width: 300px;
      height: 300px;

      border-radius: 50%;

      right: -100px;
      top: -160px;

      background:
        rgba(255,255,255,.07);

    }

    .pd-hero-left {

      position: relative;
      z-index: 2;

      max-width: 650px;

    }

    .pd-eyebrow {

      display: flex;
      align-items: center;

      gap: 9px;

      margin-bottom: 11px;

      color: #cbc8ff;

      font-size: 12px;
      font-weight: 800;

      letter-spacing: .8px;

      text-transform: uppercase;

    }

    .pd-eyebrow-dot {

      width: 8px;
      height: 8px;

      border-radius: 50%;

      background: #75e5aa;

      box-shadow:
        0 0 0 5px rgba(117,229,170,.12);

    }

    .pd-hero h1 {

      margin: 0 0 9px;

      color: white;

      font-size: 30px;
      font-weight: 850;

      line-height: 1.2;

    }

    .pd-hero p {

      max-width: 590px;

      margin: 0;

      color: rgba(255,255,255,.68);

      font-size: 14px;
      line-height: 1.7;

    }


    /* =====================================================
       HERO STATS
    ===================================================== */

    .pd-hero-stats {

      position: relative;
      z-index: 2;

      display: flex;

      border:

        1px solid rgba(255,255,255,.11);

      border-radius: 17px;

      background:
        rgba(255,255,255,.07);

      backdrop-filter: blur(10px);

      overflow: hidden;

    }

    .pd-stat {

      min-width: 120px;

      padding: 14px 18px;

      text-align: center;

    }

    .pd-stat + .pd-stat {

      border-left:
        1px solid rgba(255,255,255,.1);

    }

    .pd-stat span {

      display: block;

      color:
        rgba(255,255,255,.55);

      font-size: 10px;

      margin-bottom: 3px;

    }

    .pd-stat strong {

      color: #fff;

      font-size: 21px;

      font-weight: 850;

    }


    /* =====================================================
       SEARCH
    ===================================================== */

    .pd-search-box {

      position: relative;

      display: flex;
      align-items: center;

      max-width: 480px;

      margin-bottom: 20px;

      background: #fff;

      border:
        1px solid var(--pd-border);

      border-radius: 15px;

      box-shadow:
        0 5px 18px rgba(30,38,64,.04);

    }

    .pd-search-icon {

      width: 46px;

      text-align: center;

      color: #98a2b3;

      font-size: 21px;

    }

    .pd-search-box input {

      width: 100%;

      height: 49px;

      border: 0;
      outline: 0;

      padding: 0 12px 0 0;

      color: var(--pd-text);

      font-size: 13px;

      background: transparent;

    }

    .pd-search-clear {

      width: 38px;
      height: 38px;

      margin-right: 5px;

      border: 0;

      border-radius: 10px;

      background: #f5f6f8;

      color: #667085;

      cursor: pointer;

      font-size: 19px;

    }


    /* =====================================================
       GATEWAY SECTION
    ===================================================== */

    .pd-gateway-list {

      display: flex;
      flex-direction: column;

      gap: 20px;

    }

    .pd-gateway-section {

      overflow: hidden;

      border:
        1px solid var(--pd-border);

      border-radius: 22px;

      background: #fff;

      box-shadow:
        0 8px 30px rgba(30,38,64,.05);

    }


    /* =====================================================
       GROUP HEADER
    ===================================================== */

    .pd-group-header {

      display: flex;

      align-items: center;

      justify-content: space-between;

      gap: 20px;

      padding: 17px 20px;

      border-bottom:
        1px solid var(--pd-border);

      background:
        linear-gradient(
          180deg,
          #fff,
          #fcfcfe
        );

    }

    .pd-group-info {

      display: flex;

      align-items: center;

      gap: 13px;

    }

    .pd-group-logo {

      width: 62px;
      height: 45px;

      display: flex;

      align-items: center;

      justify-content: center;

      border:
        1px solid #eceef3;

      border-radius: 12px;

      background: #fff;

      padding: 6px;

    }

    .pd-group-logo-img {

      display: block;

      max-width: 100%;
      max-height: 100%;

      object-fit: contain;

    }

    .pd-group-label {

      display: block;

      margin-bottom: 2px;

      color: #98a2b3;

      font-size: 10px;

      font-weight: 750;

      text-transform: uppercase;

      letter-spacing: .55px;

    }

    .pd-group-info h2 {

      margin: 0;

      color: var(--pd-text);

      font-size: 17px;

      font-weight: 850;

    }

    .pd-method-count {

      padding: 7px 11px;

      border-radius: 30px;

      background:
        var(--pd-primary-soft);

      color:
        var(--pd-primary);

      font-size: 11px;

      font-weight: 800;

      white-space: nowrap;

    }


    /* =====================================================
       PAYMENT GRID
    ===================================================== */

    .pd-method-grid {

      display: grid;

      grid-template-columns:
        repeat(3, minmax(0, 1fr));

      gap: 14px;

      padding: 20px;

    }

    .pd-method-link {

      display: block;

      color: inherit;

      text-decoration: none !important;

    }

    .pd-method-link:hover {

      color: inherit;

      text-decoration: none;

    }


    /* =====================================================
       PAYMENT CARD
    ===================================================== */

    .pd-method-card {

      height: 100%;

      min-height: 185px;

      display: flex;

      flex-direction: column;

      border:
        1.5px solid #e7e9ef;

      border-radius: 17px;

      background: #fff;

      transition:
        transform .2s ease,
        border-color .2s ease,
        box-shadow .2s ease;

      overflow: hidden;

    }

    .pd-method-card:hover {

      transform:
        translateY(-4px);

      border-color:
        #c7c3ff;

      box-shadow:
        0 13px 28px rgba(55,49,120,.09);

    }

    .pd-method-top {

      position: relative;

      min-height: 115px;

      display: flex;

      align-items: center;

      justify-content: center;

      padding: 18px;

      background:
        linear-gradient(
          180deg,
          #fafbfc,
          #fff
        );

    }

    .pd-method-image-wrap {

      height: 68px;

      width: 75%;

      display: flex;

      align-items: center;

      justify-content: center;

    }

    .pd-method-image {

      display: block;

      max-width: 100%;
      max-height: 68px;

      width: auto;
      height: auto;

      object-fit: contain;

    }

    .pd-method-arrow {

      position: absolute;

      right: 12px;
      top: 12px;

      width: 30px;
      height: 30px;

      display: flex;

      align-items: center;

      justify-content: center;

      border-radius: 9px;

      background: #f3f2ff;

      color:
        var(--pd-primary);

      font-size: 17px;

      font-weight: 900;

      transition: .2s ease;

    }

    .pd-method-card:hover
    .pd-method-arrow {

      background:
        var(--pd-primary);

      color: #fff;

    }

    .pd-method-content {

      padding: 13px 15px 14px;

      border-top:
        1px solid #f0f1f4;

    }

    .pd-method-content h4 {

      min-height: 22px;

      margin: 0 0 10px;

      color: var(--pd-text);

      font-size: 14px;

      font-weight: 800;

      line-height: 1.45;

    }

    .pd-method-footer {

      display: flex;

      align-items: center;

      justify-content: space-between;

      gap: 10px;

    }

    .pd-available {

      display: inline-flex;

      align-items: center;

      gap: 6px;

      color: #475467;

      font-size: 10px;

      font-weight: 700;

    }

    .pd-available i {

      width: 7px;
      height: 7px;

      border-radius: 50%;

      background:
        var(--pd-green);

    }

    .pd-select-text {

      color:
        var(--pd-primary);

      font-size: 11px;

      font-weight: 800;

    }


    /* =====================================================
       FALLBACK IMAGE
    ===================================================== */

    .pd-image-fallback {

      width: 100%;
      height: 100%;

      display: flex;

      flex-direction: column;

      align-items: center;

      justify-content: center;

      gap: 3px;

      color: #98a2b3;

    }

    .pd-image-fallback span {

      font-size: 23px;

    }

    .pd-image-fallback small {

      font-size: 9px;

    }


    /* =====================================================
       EMPTY
    ===================================================== */

    .pd-empty-method {

      margin: 20px;

      padding: 25px;

      border:
        1px dashed #d9dce4;

      border-radius: 14px;

      background: #fafbfc;

      color: #667085;

      text-align: center;

      font-size: 12px;

    }


    /* =====================================================
       SECURITY
    ===================================================== */

    .pd-security {

      display: flex;

      align-items: flex-start;

      gap: 12px;

      margin-top: 20px;

      padding: 16px 18px;

      border:
        1px solid #d9f1e4;

      border-radius: 16px;

      background:
        #f4fbf7;

    }

    .pd-security-icon {

      flex: 0 0 31px;

      width: 31px;
      height: 31px;

      display: flex;

      align-items: center;

      justify-content: center;

      border-radius: 50%;

      background:
        var(--pd-green);

      color: #fff;

      font-size: 13px;

      font-weight: 900;

    }

    .pd-security strong {

      display: block;

      margin-bottom: 3px;

      color: #246342;

      font-size: 12px;

    }

    .pd-security p {

      margin: 0;

      color: #567566;

      font-size: 11px;

      line-height: 1.6;

    }


    /* =====================================================
       STATE
    ===================================================== */

    .pd-state-card {

      max-width: 550px;

      margin: 70px auto;

      padding: 38px 25px;

      text-align: center;

      border:
        1px solid var(--pd-border);

      border-radius: 22px;

      background: #fff;

      box-shadow:
        0 10px 35px rgba(30,38,64,.06);

    }

    .pd-state-icon {

      width: 54px;
      height: 54px;

      display: flex;

      align-items: center;

      justify-content: center;

      margin: 0 auto 15px;

      border-radius: 16px;

      background:
        var(--pd-primary-soft);

      color:
        var(--pd-primary);

      font-size: 23px;

      font-weight: 900;

    }

    .pd-state-card h3 {

      margin: 0 0 7px;

      color: var(--pd-text);

      font-size: 18px;

      font-weight: 850;

    }

    .pd-state-card p {

      max-width: 390px;

      margin: 0 auto 18px;

      color: var(--pd-muted);

      font-size: 12px;

      line-height: 1.6;

    }

    .pd-retry-btn {

      border: 0;

      border-radius: 11px;

      padding: 10px 18px;

      background:
        var(--pd-primary);

      color: #fff;

      font-size: 12px;

      font-weight: 800;

      cursor: pointer;

    }


    /* =====================================================
       LOADING
    ===================================================== */

    .pd-loading-header {

      padding: 30px;

      margin-bottom: 20px;

      border-radius: 22px;

      background: #fff;

    }

    .pd-loading-section {

      padding: 20px;

      border-radius: 22px;

      background: #fff;

    }

    .pd-loading-grid {

      display: grid;

      grid-template-columns:
        repeat(3, 1fr);

      gap: 14px;

    }

    .pd-skeleton {

      background:
        linear-gradient(
          90deg,
          #f0f1f4 25%,
          #fafafa 38%,
          #f0f1f4 63%
        );

      background-size:
        400% 100%;

      animation:
        pd-shimmer 1.3s ease infinite;

    }

    .pd-skeleton-title {

      width: 45%;
      height: 30px;

      border-radius: 8px;

      margin-bottom: 12px;

    }

    .pd-skeleton-text {

      width: 70%;
      height: 12px;

      border-radius: 6px;

    }

    .pd-skeleton-section {

      width: 200px;
      height: 35px;

      margin-bottom: 20px;

      border-radius: 8px;

    }

    .pd-skeleton-card {

      height: 185px;

      border-radius: 17px;

    }

    @keyframes pd-shimmer {

      0% {
        background-position:
          100% 0;
      }

      100% {
        background-position:
          -100% 0;
      }

    }


    /* =====================================================
       TABLET
    ===================================================== */

    @media (max-width: 991px) {

      .pd-method-grid {

        grid-template-columns:
          repeat(2, minmax(0,1fr));

      }

      .pd-loading-grid {

        grid-template-columns:
          repeat(2,1fr);

      }

      .pd-hero {

        align-items: flex-start;

        flex-direction: column;

      }

    }


    /* =====================================================
       MOBILE
    ===================================================== */

    @media (max-width: 575px) {

      .pd-page {

        padding: 12px;

      }

      .pd-hero {

        padding: 22px 18px;

        border-radius: 18px;

      }

      .pd-hero h1 {

        font-size: 23px;

      }

      .pd-hero p {

        font-size: 12px;

      }

      .pd-hero-stats {

        width: 100%;

      }

      .pd-stat {

        flex: 1;

        min-width: 0;

        padding: 12px 8px;

      }

      .pd-stat strong {

        font-size: 18px;

      }

      .pd-search-box {

        max-width: 100%;

      }

      .pd-gateway-section {

        border-radius: 17px;

      }

      .pd-group-header {

        padding: 14px;

      }

      .pd-group-logo {

        width: 53px;
        height: 40px;

      }

      .pd-group-info h2 {

        font-size: 14px;

      }

      .pd-method-grid {

        grid-template-columns:
          repeat(2, minmax(0,1fr));

        gap: 8px;

        padding: 10px;

      }

      .pd-method-card {

        min-height: 155px;

        border-radius: 13px;

      }

      .pd-method-top {

        min-height: 90px;

        padding: 12px;

      }

      .pd-method-image-wrap {

        height: 52px;

        width: 82%;

      }

      .pd-method-image {

        max-height: 52px;

      }

      .pd-method-arrow {

        width: 25px;
        height: 25px;

        top: 7px;
        right: 7px;

        border-radius: 7px;

        font-size: 14px;

      }

      .pd-method-content {

        padding: 10px;

      }

      .pd-method-content h4 {

        margin-bottom: 7px;

        font-size: 11px;

      }

      .pd-available {

        font-size: 8px;

      }

      .pd-select-text {

        font-size: 9px;

      }

      .pd-loading-grid {

        grid-template-columns:
          repeat(2,1fr);

        gap: 8px;

      }

      .pd-skeleton-card {

        height: 155px;

      }

    }

    /* =====================================================
       VERY SMALL MOBILE
    ===================================================== */

    @media (max-width: 370px) {

      .pd-method-grid {

        grid-template-columns: 1fr;

      }

      .pd-loading-grid {

        grid-template-columns: 1fr;

      }

    }

  `}</style>
);

export default PostDetails;